import React, { useState, useEffect } from 'react';
import type { ErrorBags } from './TeacherInterface';
import { TeacherValidation } from './TeacherValidation';
import CustomInput from '../../component/CustomInput/CustomInput';
import Button from '../../component/button/button';
import { updateTeacherData } from '../../api/teacher';
import { toast } from 'react-hot-toast';
import ProfileImageUpload from '../../utils/ProfileImageUpload';
import type { TeacherProps } from './TeacherInterface';
import axios from 'axios';
import '../Modal/Modal.scss';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacher: TeacherProps;
  onTeacherUpdate: (updatedTeacherData: TeacherProps) => void;
}

export const TeacherEdit: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  teacher,
  onTeacherUpdate,
}) => {
  const [formData, setFormData] = useState<TeacherProps>({
    id: teacher.id,
    first_name: teacher.first_name,
    last_name: teacher.last_name,
    email: teacher.email,
    phone_number: teacher.phone_number,
    address: teacher.address,
    profile_photo: teacher.profile_photo || null,
  });

  const [errorBags, setErrorBags] = useState<ErrorBags>({
    first_name: '',
    last_name: '',
    address: '',
    phone_number: '',
    email: '',
    password: '',
    confirm_password: '',
    profile_photo: '',
  });

  const [submitStatus, setSubmitStatus] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setFormData({
      id: teacher.id,
      first_name: teacher.first_name,
      last_name: teacher.last_name,
      email: teacher.email,
      phone_number: teacher.phone_number,
      address: teacher.address,
      profile_photo: teacher.profile_photo ? teacher.profile_photo : null,
    });
  }, [teacher]);

  if (!isOpen) return null;

  const inputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const errorMessage = TeacherValidation(name, value, formData);
    setErrorBags(prev => ({ ...prev, [name]: errorMessage }));
    setFormData({ ...formData, [name]: value });
  };

  const inputBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const errorMessage = TeacherValidation(name, value, formData);
    setErrorBags(prevErrors => ({
      ...prevErrors,
      [name]: errorMessage,
    }));
  };

  const onUpdateHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitStatus(true);
      setLoading(true);
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('first_name', formData.first_name);
      formDataToSubmit.append('last_name', formData.last_name);
      formDataToSubmit.append('address', formData.address);
      formDataToSubmit.append('phone_number', formData.phone_number);
      formDataToSubmit.append('email', formData.email);

      if (formData.profile_photo instanceof File) {
        formDataToSubmit.append('profile_photo', formData.profile_photo);
      }

      if (!teacher?.id) {
        toast.error('teacher data missing ...');
        return;
      }

      const submitData = await updateTeacherData(formDataToSubmit, teacher.id);
      if (submitData) {
        toast.success(`Teacher ${teacher.first_name}  updated successfully !`);
        const updatedTeacher = submitData.data; // Extract updated teacher data
        onTeacherUpdate(updatedTeacher);
        console.log('from updated form:', updatedTeacher);
        onClose(); // Close the modal
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.message || 'An unexpected error occurred.';
        toast.error(errorMessage);
        console.error('Error response:', error.response);
        setLoading(false);
      }
      console.log(error);
    } finally {
      setLoading(false);
      resetFormData();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    setFormData(prevData => ({
      ...prevData,
      profile_photo: file,
    }));
  };

  const resetFormData = () => {
    setErrorBags({
      first_name: '',
      last_name: '',
      address: '',
      phone_number: '',
      email: '',
      password: '',
      confirm_password: '',
      profile_photo: '',
    });
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <form onSubmit={onUpdateHandler}>
          <div className="modal-body">
            <div className="form-group text-center">
              <ProfileImageUpload
                inputChangeHandler={handleFileChange}
                setFormData={setFormData}
                setErrorBags={setErrorBags}
                imagestring={
                  formData.profile_photo ? formData.profile_photo : ''
                }
              />
              {errorBags.profile_photo && (
                <p className="text-red-500 text-xs mt-1">
                  {errorBags.profile_photo}
                </p>
              )}
            </div>
            <div className="col-span-1 text-left">
              <label htmlFor="first_name">
                First Name <span className="text-red-500">*</span>
              </label>
              <CustomInput
                placeholder="First name"
                value={formData.first_name}
                name="first_name"
                onChange={inputChangeHandler}
                onBlur={inputBlurHandler}
              />
              {errorBags.first_name && (
                <p className="text-red-500 text-xs mt-1">
                  {errorBags.first_name}
                </p>
              )}
            </div>

            {/* Last Name */}
            <div className="col-span-1 text-left">
              <label htmlFor="last_name">
                Last Name <span className="text-red-500">*</span>
              </label>
              <CustomInput
                placeholder="Last name"
                value={formData.last_name}
                name="last_name"
                onChange={inputChangeHandler}
                onBlur={inputBlurHandler}
              />
              {errorBags.last_name && (
                <p className="text-red-500 text-xs mt-1">
                  {errorBags.last_name}
                </p>
              )}
            </div>

            {/* Address */}
            <div className="col-span-1 md:col-span-2 text-left">
              <label htmlFor="address">Address</label>
              <CustomInput
                placeholder="Address"
                value={formData.address}
                name="address"
                onChange={inputChangeHandler}
                onBlur={inputBlurHandler}
              />
              {errorBags.address && (
                <p className="text-red-500 text-xs mt-1">{errorBags.address}</p>
              )}
            </div>

            {/* Phone Number */}
            <div className="col-span-1 text-left">
              <label htmlFor="phone_number">Phone Number</label>
              <CustomInput
                placeholder="Phone number"
                value={formData.phone_number}
                name="phone_number"
                onChange={inputChangeHandler}
                onBlur={inputBlurHandler}
              />
              {errorBags.phone_number && (
                <p className="text-red-500 text-xs mt-1">
                  {errorBags.phone_number}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="col-span-1 text-left">
              <label htmlFor="email">
                Email Address <span className="text-red-500">*</span>
              </label>
              <CustomInput
                placeholder="Email address"
                value={formData.email}
                name="email"
                onChange={inputChangeHandler}
                onBlur={inputBlurHandler}
              />
              {errorBags.email && (
                <p className="text-red-500 text-xs mt-1">{errorBags.email}</p>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <div className="col-span-1 md:col-span-2 text-right flex justify-end gap-2">
              <Button
                text={loading ? 'Updating...' : 'UPDATE'}
                buttonType="submit"
                disabled={Object.values(errorBags).some(error => error !== '')}
                style={{
                  height: 45,
                  fontSize: 14,
                }}
              />
              <Button
                text="Close"
                buttonType="button"
                type="secondary"
                onClick={onClose}
                style={{
                  height: 45,
                  fontSize: 14,
                }}
              />
            </div>
          </div>

          {/* Submit Button */}
          {/* <div className="col-span-1 md:col-span-2 mt-4 text-right">
        <Button
          text={loading ? "Updating..." : "UPDATE"}
          buttonType="submit"
          disabled={Object.values(errorBags).some((error) => error !== "")}
          
        />
      </div> */}
        </form>
      </div>
    </div>
  );
};
